// Top-level React error boundary. Catches render/lifecycle crashes anywhere in
// the tree, reports them to telemetry (with the component stack), and shows a
// minimal recover-by-reload fallback instead of a blank white screen.

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { captureError } from '@/lib/telemetry';

interface Props {
  children: ReactNode;
}
interface State {
  hasError: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    captureError({
      error_kind: 'react_error',
      message: error?.message ?? 'React render error',
      stack: `${error?.stack ?? ''}\n--- component stack ---${info?.componentStack ?? ''}`,
    });
  }

  override render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 14,
          minHeight: '60vh',
          padding: 24,
          textAlign: 'center',
        }}
      >
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 800 }}>
          Something went wrong
        </div>
        <div className="muted" style={{ fontSize: 13, maxWidth: 420 }}>
          The error has been reported. Reloading usually fixes it — if it keeps happening, let the
          team know.
        </div>
        <button className="btn btn-primary" onClick={() => window.location.reload()}>
          Reload
        </button>
      </div>
    );
  }
}
